import { forwardRef, useLayoutEffect, useRef, useState, type ElementRef } from "react";

import { useWindowDimensions } from "@luce/core";

import { Divider } from "../../atoms/divider";
import {
  dropdownContentVariants,
  dropdownItemVariants,
} from "../../atoms/dropdown/dropdown.variants";
import { Icon } from "../../atoms/icons";
import { selectContentMaxHeight } from "../../atoms/select/lib";
import {
  textFieldContainerVariants,
  textFieldInputClass,
  textFieldPlaceholderClass,
} from "../../atoms/text-field";
import { textFieldInputReachClass } from "../../atoms/text-field/lib";
import { cn, platformProps } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import {
  Box,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  Image,
  Pressable,
  TextInput,
} from "../../primitives";
import { iconSize } from "../../tokens";
import { CountryRowContent } from "./country-row";
import { CountrySheet } from "./country-sheet";
import { countryRowSelectedClassName } from "./lib";
import type { PhoneNumberFieldProps } from "./types";
import { usePhoneNumberField } from "./use-phone-number-field";

const PhoneNumberField = forwardRef<ElementRef<typeof TextInput>, PhoneNumberFieldProps>(
  (
    {
      countries,
      initialCountryCode,
      value,
      onChangePhoneNumber,
      size = "sm",
      disabled = false,
      readOnly = false,
      state = "default",
      className,
      testID,
      id,
      placeholder = "Phone number",
      accessibilityLabel = "Phone number with country code",
      keyboardType = "phone-pad",
      onFocus,
      onBlur,
      ...textInputProps
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const fieldRef = useRef<HTMLElement>(null);
    const [listBox, setListBox] = useState<{ width: number; alignOffset: number }>();
    // Below 768 the picker is the bottom sheet, as on iOS and Android; wider,
    // the dropdown (Ardhi's DQ25 answer, 2026-11-02)
    const { isMobile } = useWindowDimensions();
    // Crossing 768 swaps the picker and its trigger, so an open picker closes
    // rather than moving to the other surface
    const [pickerIsSheet, setPickerIsSheet] = useState(isMobile);
    if (pickerIsSheet !== isMobile) {
      setPickerIsSheet(isMobile);
      setOpen(false);
    }

    const {
      availableCountries,
      resolvedCountry,
      displayValue,
      visualState,
      handleInputChange,
      handleCountrySelect,
    } = usePhoneNumberField({
      countries,
      initialCountryCode,
      value,
      onChangePhoneNumber,
      disabled,
      state,
      isFocused,
      open,
    });

    // Read-only locks the country too (PhoneNumberField #2): the list doesn't
    // open, and a pick made while it's open changes nothing
    const locked = disabled || readOnly;

    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen && locked) return;
      setOpen(nextOpen);
    };

    // The list is as wide as the field and lines up with it, as Figma draws
    // it (PhoneNumberField #13). Radix sizes and aligns it by the trigger,
    // which is only the flag, inside the field's border: measured each time it
    // opens
    useLayoutEffect(() => {
      const field = fieldRef.current;
      if (!open || isMobile || !field) return;
      setListBox({
        width: field.getBoundingClientRect().width,
        alignOffset: -parseFloat(getComputedStyle(field).borderLeftWidth),
      });
    }, [open, isMobile]);

    const onSelectCountry = (nextCountry: typeof resolvedCountry) => {
      if (!locked) handleCountrySelect(nextCountry);
      setOpen(false);
    };

    // Touch screens: the small 40px field's trigger takes taps 2px past its top
    // and bottom, to 44 (S38); the medium one is 48 already. Keyboard focus
    // draws Select's ring, with the field's left corners. Figma's segment: 8
    // each side of the flag and caret (153:1649; PhoneNumberField #8)
    const triggerClassName = cn(
      "gap-spacing-xs px-spacing-sm relative flex h-full shrink-0 flex-row items-center justify-center rounded-s-[inherit] border-0 bg-transparent outline-none",
      "web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-focus web:focus-visible:ring-offset-focus",
      minTouchTargetClassName,
    );
    const triggerLabel = `Country code selector, ${resolvedCountry.name}, ${resolvedCountry.dialCode}`;
    const segment = (
      <Box className="gap-spacing-xs flex flex-row items-center">
        <Image src={resolvedCountry.flagUrl} width={30} height={20} alt={resolvedCountry.name} />
        <Icon
          name={open ? "CaretUp" : "CaretDown"}
          size={iconSize.xs}
          className="text-luce-neutral-foreground-intermediate"
        />
      </Box>
    );

    return (
      <>
        <DropdownMenuRoot open={!isMobile && open} onOpenChange={handleOpenChange} modal>
          <Box
            ref={fieldRef}
            {...platformProps({ slot: "phone-number-field" })}
            // ARIA gives a role-less div no name, so a label needs a group
            accessibilityRole={accessibilityLabel ? "group" : undefined}
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ disabled }}
            className={cn(
              textFieldContainerVariants({ size, visualState }),
              // Figma draws the segment, divider and number edge to edge, each
              // with its own padding (153:1712; PhoneNumberField #8)
              "gap-spacing-0 px-spacing-0 py-spacing-0 md:px-spacing-0 md:py-spacing-0",
              "overflow-visible",
              className,
            )}
          >
            {/* Only the trigger changes at 768, so the number keeps its focus
              when a phone turns sideways */}
            {isMobile ? (
              <Pressable
                disabled={locked}
                onClick={() => handleOpenChange(true)}
                accessibilityRole="button"
                accessibilityState={{ disabled: locked, expanded: open }}
                aria-haspopup="dialog"
                className={triggerClassName}
                accessibilityLabel={triggerLabel}
              >
                {segment}
              </Pressable>
            ) : (
              <DropdownMenuTrigger
                disabled={locked}
                className={triggerClassName}
                accessibilityLabel={triggerLabel}
              >
                {segment}
              </DropdownMenuTrigger>
            )}
            <Divider orientation="vertical" emphasis="medium" className="h-full" />
            <TextInput
              ref={ref}
              // The input alone carries testID, as TextField's does (PhoneNumberField #20)
              {...platformProps({ testID, id })}
              {...textInputProps}
              value={displayValue}
              disabled={disabled}
              readOnly={readOnly}
              invalid={state === "error"}
              placeholder={placeholder}
              keyboardType={keyboardType}
              accessibilityLabel={accessibilityLabel}
              className={cn(
                // TextField's input typography (PhoneNumberField #7)
                "text-body-md md:text-body-md-dt native:font-inter-regular",
                // Figma's 16 on each side of the text (153:1714)
                "px-spacing-lg flex-1 self-stretch bg-transparent outline-none",
                // A small field's input takes taps 44 tall, as TextField's (Form #18)
                textFieldInputReachClass[size],
                textFieldInputClass[visualState],
                textFieldPlaceholderClass[visualState],
              )}
              onChangeText={handleInputChange}
              onFocus={() => {
                setIsFocused(true);
                onFocus?.();
              }}
              onBlur={() => {
                setIsFocused(false);
                onBlur?.();
              }}
            />
          </Box>
          {isMobile ? null : (
            <DropdownMenuPortal>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className={cn(dropdownContentVariants(), "p-spacing-xs")}
                alignOffset={listBox?.alignOffset}
                style={listBox ? { width: listBox.width } : undefined}
                accessibilityLabel="Country code options"
              >
                {/* Select's height limit (PhoneNumberField #13) */}
                <Box
                  style={{ maxHeight: selectContentMaxHeight }}
                  className="overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  <DropdownMenuGroup>
                    {availableCountries.map((option) => {
                      const isSelected = option.code === resolvedCountry.code;
                      return (
                        <DropdownMenuItem
                          key={option.code}
                          onSelect={() => onSelectCountry(option)}
                          accessibilityLabel={`${option.name}, ${option.dialCode}`}
                          className={cn(
                            dropdownItemVariants({ disabled: false, variant: "default" }),
                            "px-spacing-lg py-spacing-md",
                            isSelected && countryRowSelectedClassName,
                          )}
                        >
                          <Box className="gap-spacing-md flex w-full flex-row items-center justify-between">
                            <CountryRowContent country={option} selected={isSelected} />
                          </Box>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </Box>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          )}
        </DropdownMenuRoot>
        {isMobile ? (
          <CountrySheet
            open={open}
            onOpenChange={handleOpenChange}
            countries={availableCountries}
            selectedCode={resolvedCountry.code}
            onSelect={onSelectCountry}
            // One detent: at 40% the web sheet can't scroll its last rows
            // into view (BottomSheet's own gap)
            detents={["max"]}
          />
        ) : null}
      </>
    );
  },
);

PhoneNumberField.displayName = "PhoneNumberField";

export { PhoneNumberField };
